import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { MdAdd, MdDelete, MdSave, MdLayers } from 'react-icons/md';
import { format } from 'date-fns';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';

async function parseXlsxToRows(file) {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheetName = workbook.SheetNames.includes('Sheet1') ? 'Sheet1' : workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true });
}

function emptyLayer() {
    return { wFile: null, wParsed: null, wRows: 0, wCols: 0, wError: null, bFile: null, bParsed: null, bRows: 0, bError: null };
}

export default function YsaParamsTab({ companyId }) {
    const { t } = useLanguage();
    const [layers, setLayers] = useState([emptyLayer()]);
    const [modelName, setModelName] = useState('');
    const [saving, setSaving] = useState(false);
    const [models, setModels] = useState([]);
    const [loadingModels, setLoadingModels] = useState(true);
    const [deletingId, setDeletingId] = useState(null);

    const fetchModels = useCallback(async () => {
        try {
            setLoadingModels(true);
            const res = await api.get(`/ysa/company/${companyId}/models`);
            setModels(res.data.data || []);
        } catch {
            toast.error(t('ysa.modelsLoadFailed'));
        } finally {
            setLoadingModels(false);
        }
    }, [companyId, t]);

    useEffect(() => { fetchModels(); }, [fetchModels]);

    const handleWFile = async (index, file) => {
        try {
            const rows = await parseXlsxToRows(file);
            const cols = rows[0]?.length || 0;
            const rectangular = rows.every(r => r.length === cols);
            const numeric = rows.every(r => r.every(v => typeof v === 'number' && !Number.isNaN(v)));
            const error = (!rows.length || !rectangular || !numeric) ? t('ysa.invalidMatrixFile') : null;
            setLayers(ls => ls.map((l, i) => i === index ? { ...l, wFile: file, wParsed: error ? null : rows, wRows: rows.length, wCols: cols, wError: error } : l));
        } catch {
            setLayers(ls => ls.map((l, i) => i === index ? { ...l, wFile: file, wParsed: null, wError: t('ysa.invalidMatrixFile') } : l));
        }
    };

    const handleBFile = async (index, file) => {
        try {
            const rows = await parseXlsxToRows(file);
            const singleColumn = rows.every(r => r.length === 1);
            const numeric = rows.every(r => typeof r[0] === 'number' && !Number.isNaN(r[0]));
            const error = (!rows.length || !singleColumn || !numeric) ? t('ysa.invalidVectorFile') : null;
            const flat = rows.map(r => r[0]);
            setLayers(ls => ls.map((l, i) => i === index ? { ...l, bFile: file, bParsed: error ? null : flat, bRows: rows.length, bError: error } : l));
        } catch {
            setLayers(ls => ls.map((l, i) => i === index ? { ...l, bFile: file, bParsed: null, bError: t('ysa.invalidVectorFile') } : l));
        }
    };

    const addLayer = () => setLayers(ls => [...ls, emptyLayer()]);
    const removeLayer = (index) => setLayers(ls => ls.filter((_, i) => i !== index));

    // Chain-validate shapes across layers for live feedback.
    const chainErrors = layers.map((layer, i) => {
        if (layer.wError) return layer.wError;
        if (layer.bError) return layer.bError;
        if (!layer.wParsed || !layer.bParsed) return null;
        if (layer.wRows !== layer.bRows) {
            return t('ysa.shapeMismatchInternal', { layer: i + 1, wRows: layer.wRows, bRows: layer.bRows });
        }
        if (i > 0) {
            const prev = layers[i - 1];
            if (prev.wParsed && layer.wCols !== prev.wRows) {
                return t('ysa.shapeMismatch', { layer: i + 1, got: layer.wCols, expected: prev.wRows });
            }
        }
        return null;
    });

    const allLayersReady = layers.length > 0 && layers.every(l => l.wParsed && l.bParsed);
    const hasErrors = chainErrors.some(Boolean);

    // Normalization (colminx/colmaxx/colminy/colmaxy) is never selected or typed in by
    // hand — the server always applies the same fixed constants, carried over from the
    // original calibration (see FIXED_INPUT_MAX/FIXED_OUTPUT_MAX in routes/ysa.js).
    // They're defined for exactly 4 columns, so the only requirement here is that the
    // uploaded W/B matrices resolve to exactly 4 inputs and 4 outputs.
    const FIXED_INPUT_MAX = [0.763, 0.3469, 0.973, 0.828];
    const FIXED_OUTPUT_MAX = [0.56, 0.47, 0.618, 0.76];
    const modelInputSize = layers[0]?.wParsed ? layers[0].wCols : null;
    const modelOutputSize = layers[layers.length - 1]?.wParsed ? layers[layers.length - 1].wRows : null;
    const sizeMismatch =
        (modelInputSize !== null && modelInputSize !== FIXED_INPUT_MAX.length) ||
        (modelOutputSize !== null && modelOutputSize !== FIXED_OUTPUT_MAX.length);

    const canSave = allLayersReady && !hasErrors && !sizeMismatch && modelName.trim() && !saving;

    const handleSaveModel = async () => {
        if (!canSave) return;
        try {
            setSaving(true);
            await api.post(`/ysa/company/${companyId}/models`, {
                name: modelName.trim(),
                layers: layers.map(l => ({ w: l.wParsed, b: l.bParsed }))
            });
            toast.success(t('ysa.modelSaved'));
            setLayers([emptyLayer()]);
            setModelName('');
            await fetchModels();
        } catch (err) {
            toast.error(err.response?.data?.message || t('ysa.modelSaveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteModel = async (model) => {
        if (!window.confirm(t('ysa.deleteModelConfirm', { name: model.name }))) return;
        try {
            setDeletingId(model._id);
            await api.delete(`/ysa/company/${companyId}/models/${model._id}`);
            toast.success(t('ysa.modelDeleted'));
            await fetchModels();
        } catch (err) {
            toast.error(err.response?.data?.message || t('ysa.modelDeleteFailed'));
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div className="card" style={{ padding: 20, flex: '1 1 480px' }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <MdLayers style={{ color: 'var(--primary)' }} /> {t('ysa.tabParams')}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 18 }}>
                    {layers.map((layer, i) => (
                        <div key={i} style={{ padding: 14, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-body)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <span style={{ fontWeight: 700, fontSize: 13 }}>{t('ysa.layer')} {i + 1}</span>
                                {layers.length > 1 && (
                                    <button className="btn btn-danger btn-sm btn-icon" onClick={() => removeLayer(i)} style={{ width: 26, height: 26 }}>
                                        <MdDelete size={14} />
                                    </button>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                <div style={{ flex: '1 1 200px' }}>
                                    <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>{t('ysa.wFile')}</label>
                                    <input type="file" accept=".xlsx" onChange={e => e.target.files[0] && handleWFile(i, e.target.files[0])} style={{ fontSize: 12 }} />
                                    {layer.wParsed && !layer.wError && (
                                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--success)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
                                            {t('ysa.matrixDims', { rows: layer.wRows, cols: layer.wCols })}
                                        </div>
                                    )}
                                </div>
                                <div style={{ flex: '1 1 200px' }}>
                                    <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>{t('ysa.bFile')}</label>
                                    <input type="file" accept=".xlsx" onChange={e => e.target.files[0] && handleBFile(i, e.target.files[0])} style={{ fontSize: 12 }} />
                                    {layer.bParsed && !layer.bError && (
                                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--success)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
                                            {t('ysa.matrixDims', { rows: layer.bRows, cols: 1 })}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {chainErrors[i] && (
                                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--danger)', fontWeight: 600 }}>{chainErrors[i]}</div>
                            )}
                        </div>
                    ))}
                </div>

                <button className="btn btn-secondary btn-sm" onClick={addLayer} style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MdAdd /> {t('ysa.addLayer')}
                </button>

                {/* Normalization constants block intentionally hidden from the UI — the
                    fixed-constant validation (sizeMismatch → canSave) still runs in the
                    background exactly as before, just without surfacing the numbers/hint. */}

                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input
                        className="form-input"
                        placeholder={t('ysa.modelNamePlaceholder')}
                        value={modelName}
                        onChange={e => setModelName(e.target.value)}
                        style={{ flex: 1 }}
                    />
                    <button className="btn btn-primary btn-sm" onClick={handleSaveModel} disabled={!canSave} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {saving ? <span className="spinner spinner-sm" /> : <MdSave />} {t('ysa.save')}
                    </button>
                </div>
            </div>

            <div className="card" style={{ padding: 20, flex: '1 1 320px' }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>{t('ysa.savedModels')}</div>
                {loadingModels ? (
                    <div className="spinner spinner-sm" />
                ) : models.length === 0 ? (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('ysa.noModels')}</div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {models.map(model => {
                            const arch = [
                                model.layers?.[0]?.inputSize,
                                ...(model.layers || []).map(l => l.outputSize)
                            ].filter(v => v !== undefined).join(' → ');
                            return (
                                <div key={model._id} style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-body)'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{model.name}</div>
                                        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                                            {arch} · {format(new Date(model.createdAt), 'yyyy/MM/dd HH:mm')}
                                        </div>
                                    </div>
                                    <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDeleteModel(model)} disabled={deletingId === model._id} style={{ width: 28, height: 28 }}>
                                        {deletingId === model._id ? <span className="spinner spinner-sm" /> : <MdDelete size={14} />}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
